export const blogPosts = [
  {
    slug: "qu-est-ce-que-l-ia",
    title: "Qu'est-ce que l'intelligence artificielle ? Guide complet 2025",
    seoTitle: "Qu'est-ce que l'IA ? Définition, types, exemples | YAFORMIS",
    metaDescription: "Comprenez ce qu'est l'intelligence artificielle, ses différents types (machine learning, deep learning, IA générative) et ses applications concrètes en entreprise.",
    excerpt:
      "L'intelligence artificielle bouleverse tous les secteurs. Mais que recouvre vraiment ce terme ? Plongez dans une définition claire, accessible et illustrée d'exemples concrets.",
    cover: "/illustrations/initiation-ia.png",
    category: "Fondamentaux",
    readingTime: "8 min",
    publishedAt: "2025-11-15",
    author: "Équipe pédagogique YAFORMIS",
    content: [
      { type: "p", text: "L'intelligence artificielle (IA) désigne l'ensemble des techniques permettant à une machine de réaliser des tâches qui requièrent normalement l'intelligence humaine : comprendre un texte, reconnaître une image, prendre une décision, apprendre de l'expérience." },
      { type: "h2", text: "Une brève histoire de l'IA" },
      { type: "p", text: "Le terme a été inventé en 1956 lors de la conférence de Dartmouth. Pendant des décennies, l'IA est restée confinée aux laboratoires. L'explosion récente vient de trois facteurs : la quantité massive de données disponibles, la puissance de calcul (GPU), et les avancées des réseaux de neurones profonds (deep learning)." },
      { type: "h2", text: "Les grands types d'IA" },
      { type: "h3", text: "1. Le machine learning (apprentissage automatique)" },
      { type: "p", text: "Au lieu d'être programmées avec des règles explicites, les machines apprennent à partir d'exemples. Un filtre anti-spam, par exemple, apprend à reconnaître les emails indésirables en analysant des milliers d'exemples." },
      { type: "h3", text: "2. Le deep learning (apprentissage profond)" },
      { type: "p", text: "C'est une branche du machine learning qui utilise des réseaux de neurones à plusieurs couches. Le deep learning excelle pour la vision (reconnaître un visage), la voix (Siri, Alexa) et le langage." },
      { type: "h3", text: "3. L'IA générative" },
      { type: "p", text: "C'est la révolution de 2022-2025. Les modèles comme ChatGPT, Claude, Midjourney ou Sora ne se contentent plus d'analyser : ils créent du texte, des images, des vidéos, du code. Ce sont les Large Language Models (LLM) et leurs équivalents multimodaux." },
      { type: "h2", text: "Cas d'usage concrets en entreprise" },
      { type: "ul", items: [
        "Marketing : génération de contenus, segmentation client, A/B testing automatisé",
        "Vente : qualification de leads, propositions commerciales personnalisées",
        "RH : tri de CV (avec garde-fous), onboarding automatisé",
        "Support client : chatbots intelligents, résumés de tickets",
        "Finance : détection de fraude, analyse de documents",
      ] },
      { type: "h2", text: "Quelles compétences développer ?" },
      { type: "p", text: "L'IA ne remplacera pas les professionnels, mais les professionnels qui maîtrisent l'IA remplaceront ceux qui ne la maîtrisent pas. Notre formation Initiation à l'IA vous donne les bases en 2 jours, sans aucun prérequis technique." },
    ],
  },
  {
    slug: "comment-utiliser-chatgpt",
    title: "Comment utiliser ChatGPT efficacement : guide pratique 2025",
    seoTitle: "Comment utiliser ChatGPT en 2025 : guide pratique pour professionnels | YAFORMIS",
    metaDescription: "Maîtrisez ChatGPT en 10 minutes. Prompts efficaces, cas d'usage métiers, erreurs à éviter. Guide complet pour professionnels.",
    excerpt:
      "ChatGPT est l'outil IA le plus utilisé au monde. Mais l'utiliser en surface ou en expert change tout. Voici les techniques que nous enseignons en formation.",
    cover: "/illustrations/outils-ia.png",
    category: "Outils IA",
    readingTime: "10 min",
    publishedAt: "2025-11-22",
    author: "Équipe pédagogique YAFORMIS",
    content: [
      { type: "p", text: "ChatGPT compte plus de 800 millions d'utilisateurs actifs. Pourtant, la majorité l'utilise comme un moteur de recherche conversationnel — soit moins de 10% de son potentiel." },
      { type: "h2", text: "L'anatomie d'un bon prompt" },
      { type: "p", text: "Un prompt efficace contient quatre éléments : un rôle, un contexte, une tâche, un format attendu." },
      { type: "ul", items: [
        "Rôle : « Tu es un expert en marketing B2B SaaS »",
        "Contexte : « Notre cible : DAF de PME 50-200 personnes »",
        "Tâche : « Rédige 3 lignes d'objet d'email de prospection »",
        "Format : « Maximum 60 caractères chacune, ton direct »",
      ] },
      { type: "h2", text: "Les 5 techniques avancées" },
      { type: "h3", text: "1. Le Chain-of-Thought" },
      { type: "p", text: "Demandez à ChatGPT de raisonner étape par étape avant de répondre. Cela améliore drastiquement la qualité sur les tâches de réflexion." },
      { type: "h3", text: "2. Le Few-Shot Prompting" },
      { type: "p", text: "Donnez 2 ou 3 exemples du résultat attendu avant de demander une nouvelle production. Le modèle calque alors le style." },
      { type: "h3", text: "3. Les rôles multiples" },
      { type: "p", text: "Demandez à ChatGPT de jouer plusieurs rôles successivement (rédacteur, puis correcteur, puis avocat) pour avoir un résultat affiné." },
      { type: "h3", text: "4. Les contraintes explicites" },
      { type: "p", text: "« Pas plus de 200 mots », « N'utilise pas le mot X », « Style Hemingway » : plus c'est précis, mieux c'est." },
      { type: "h3", text: "5. L'itération guidée" },
      { type: "p", text: "Ne vous contentez jamais de la première réponse. Répondez « affine », « plus court », « plus percutant ». L'IA s'adapte." },
      { type: "h2", text: "Erreurs fréquentes" },
      { type: "ul", items: [
        "Faire confiance aveuglément : ChatGPT hallucine. Toujours vérifier les chiffres et faits.",
        "Coller des données confidentielles client : utilisez ChatGPT Team ou Enterprise.",
        "Vouloir du texte « générique » : trop court ou trop vague produit du contenu fade.",
        "Ne pas itérer : la première réponse est rarement la meilleure.",
      ] },
      { type: "p", text: "Pour aller plus loin, notre formation Outils IA détaille 50+ prompts métier prêts à l'emploi en 3 jours." },
    ],
  },
  {
    slug: "meilleurs-outils-ia-2025",
    title: "Les 12 meilleurs outils IA en 2025 (par cas d'usage)",
    seoTitle: "Top 12 outils IA 2025 : ChatGPT, Claude, Midjourney, Sora… | YAFORMIS",
    metaDescription: "Découvrez les 12 meilleurs outils IA professionnels en 2025 classés par cas d'usage : texte, image, vidéo, voix, code, automatisation.",
    excerpt:
      "Le marché des outils IA explose. Voici notre sélection 2025, testée et utilisée quotidiennement par nos formateurs en entreprise.",
    cover: "/illustrations/ia-generative.png",
    category: "Outils IA",
    readingTime: "12 min",
    publishedAt: "2025-12-01",
    author: "Équipe pédagogique YAFORMIS",
    content: [
      { type: "p", text: "Cette sélection s'appuie sur notre veille hebdomadaire et l'usage réel de nos formateurs et 200+ clients en 2025. Tous les outils mentionnés disposent d'un plan gratuit ou d'un essai." },
      { type: "h2", text: "Génération de texte (LLM)" },
      { type: "ul", items: [
        "ChatGPT (GPT-5.2) : le plus polyvalent, excellente base.",
        "Claude Sonnet 4.5 : le meilleur pour rédaction longue, analyse de documents et code.",
        "Gemini 3 Pro : intégration Google Workspace, multimodal puissant.",
        "Mistral Le Chat : alternative française, hébergement EU.",
      ] },
      { type: "h2", text: "Recherche augmentée" },
      { type: "ul", items: [
        "Perplexity : Google killer pour la recherche sourcée.",
        "You.com : agents de recherche multi-étapes.",
      ] },
      { type: "h2", text: "Génération d'images" },
      { type: "ul", items: [
        "Midjourney v7 : qualité artistique inégalée.",
        "Nano Banana (Gemini Image) : édition d'image intelligente, conversation.",
        "Flux : ultra-réaliste, open source.",
      ] },
      { type: "h2", text: "Vidéo et voix" },
      { type: "ul", items: [
        "Sora 2 : génération vidéo cinématographique.",
        "ElevenLabs : voix synthétique réaliste, clonage de voix.",
        "HeyGen : avatars vidéo personnalisables.",
      ] },
      { type: "h2", text: "Code et développement" },
      { type: "ul", items: [
        "Cursor : éditeur IA, alternative à VS Code.",
        "Claude Code : agent IA pour développeurs.",
        "GitHub Copilot : intégration IDE classique.",
      ] },
      { type: "h2", text: "Automatisation et agents" },
      { type: "ul", items: [
        "n8n : workflows IA no-code, self-hostable.",
        "Make (ex Integromat) : automation visuelle.",
        "LangChain : framework code pour agents complexes.",
      ] },
      { type: "p", text: "Pour apprendre à les combiner intelligemment et construire votre stack IA personnelle, découvrez notre formation Outils IA." },
    ],
  },
  {
    slug: "creer-agent-ia-facilement",
    title: "Créer un agent IA facilement en 2025 : méthode pas à pas",
    seoTitle: "Comment créer un agent IA facilement (no-code & code) | YAFORMIS",
    metaDescription: "Méthode complète pour créer un agent IA fonctionnel : architecture, outils no-code et code, sécurité, déploiement. Guide pas à pas.",
    excerpt:
      "Un agent IA n'est plus réservé aux ingénieurs. Avec les bons outils, vous pouvez créer un agent qualifiant vos leads, répondant à vos clients ou pilotant vos process en quelques heures.",
    cover: "/illustrations/agents-ia.png",
    category: "Agents IA",
    readingTime: "11 min",
    publishedAt: "2025-12-08",
    author: "Équipe pédagogique YAFORMIS",
    content: [
      { type: "p", text: "Un agent IA, contrairement à un simple chatbot, est capable de prendre des décisions et d'agir : envoyer un email, mettre à jour un CRM, déclencher un paiement, fouiller le web." },
      { type: "h2", text: "L'architecture d'un agent" },
      { type: "p", text: "Tout agent fonctionne selon une boucle : perception → décision → action → observation. Quatre briques sont essentielles : un LLM (le cerveau), une mémoire (court et long terme), des outils (les mains) et un orchestrateur (qui décide quand utiliser quoi)." },
      { type: "h2", text: "Étape 1 — Définir le périmètre" },
      { type: "p", text: "Erreur classique : vouloir un agent qui fait tout. Commencez petit : « Un agent qui qualifie chaque lead entrant et l'enrichit dans le CRM ». Mesurable, démontrable, déployable." },
      { type: "h2", text: "Étape 2 — Choisir la stack" },
      { type: "h3", text: "Voie no-code (recommandé pour débuter)" },
      { type: "ul", items: [
        "n8n + OpenAI / Claude : le plus puissant, courbe d'apprentissage moyenne.",
        "Make : très visuel, parfait pour démarrer.",
        "Voiceflow : agents conversationnels (chat, voix).",
      ] },
      { type: "h3", text: "Voie code (pour aller plus loin)" },
      { type: "ul", items: [
        "LangChain / LangGraph (Python ou JS) : standard du marché.",
        "CrewAI : pour les agents multi-rôles collaboratifs.",
        "OpenAI Assistants API : intégration native.",
      ] },
      { type: "h2", text: "Étape 3 — Connecter aux données" },
      { type: "p", text: "Un agent sans accès à vos données est un agent inutile. Connectez-le à votre CRM (HubSpot, Salesforce), votre Drive, votre base produit. Utilisez des bases vectorielles (Pinecone, Qdrant, Supabase) pour la mémoire long terme." },
      { type: "h2", text: "Étape 4 — Sécuriser" },
      { type: "ul", items: [
        "Guardrails : restreignez les actions possibles (« interdit de supprimer », « max 10€ par décision »).",
        "Logs et traces : indispensable pour le debug. Outils : Langfuse, LangSmith.",
        "Validation humaine : pour les actions critiques, prévoyez un humain dans la boucle.",
      ] },
      { type: "h2", text: "Étape 5 — Itérer" },
      { type: "p", text: "Aucun agent ne fonctionne parfaitement à V1. Mesurez : taux de succès, coût par tâche, satisfaction utilisateur. Ajustez les prompts, ajoutez des outils, raffinez la mémoire." },
      { type: "p", text: "Notre formation Agents IA (5 jours) vous fait passer de 0 à un agent déployé en production sur votre propre stack. Niveau intermédiaire à avancé." },
    ],
  },
];

export const getBlogBySlug = (slug) => blogPosts.find((p) => p.slug === slug);
